import type {
  CalibreFamilyConfidence,
  FunctionalCompetitorSuggestion,
  SuggestCalibreFamilyResult,
} from '../../services/firebase';

/**
 * AI suggestions the editor is willing to show, keyed by the field each one
 * targets. Absent keys were either not returned by the model or held back by the
 * confidence gate below.
 */
export interface SuggestionState {
  description?: { value: string; confidence?: CalibreFamilyConfidence };
  baseCalibre?: {
    calibreId?: string;
    name?: string;
    confidence?: CalibreFamilyConfidence;
  };
  inspiredBy?: { hint: string; confidence?: CalibreFamilyConfidence };
  functionalCompetitor?: FunctionalCompetitorSuggestion[];
}

/** What the curator currently has in the editor, for the gate to compare against. */
export interface CalibreFamilyDraft {
  description: string;
  baseCalibreId: string;
  hasInspiredBy: boolean;
  competitorCount: number;
}

export interface GatedSuggestions {
  next: SuggestionState;
  /**
   * Human-readable field labels the gate suppressed. The editor surfaces these
   * as an "AI unsure — keeping your value" note so a held-back suggestion is
   * visibly a decision rather than an empty response.
   */
  heldBack: string[];
}

/**
 * Suppress low-confidence suggestions for fields the curator has already filled.
 *
 * The callable is asked to return null over a researched value, but a prompt is
 * a request, not a guarantee — the Messages API doesn't enforce it and a live
 * admin_config/ai_prompts override can weaken the instruction at any time. This
 * is the enforcement point.
 *
 * The rule is deliberately one-sided: `low` confidence only loses against an
 * existing value. On an EMPTY field a low-confidence hint still beats nothing,
 * and high/medium suggestions are always offered — the curator keeps the final
 * say, they just aren't asked to adjudicate the model's own guesswork against
 * their research.
 */
export function gateSuggestions(
  result: SuggestCalibreFamilyResult,
  draft: CalibreFamilyDraft,
): GatedSuggestions {
  const next: SuggestionState = {};
  const heldBack: string[] = [];

  const isLow = (c?: CalibreFamilyConfidence) => c === 'low';

  if (result.description?.value) {
    if (draft.description.trim() && isLow(result.description.confidence)) {
      heldBack.push('Description');
    } else {
      next.description = result.description;
    }
  }

  const base = result.baseCalibre;
  if (base && (base.calibreId || base.name)) {
    if (draft.baseCalibreId && isLow(base.confidence)) {
      heldBack.push('Base calibre');
    } else {
      next.baseCalibre = base;
    }
  }

  if (result.inspiredBy?.hint) {
    if (draft.hasInspiredBy && isLow(result.inspiredBy.confidence)) {
      heldBack.push('Inspired by');
    } else {
      next.inspiredBy = result.inspiredBy;
    }
  }

  // Competitors are additive rather than a single value, so the gate only bites
  // once the curator has some: a low-confidence rival appended to a researched
  // list is noise, but on an empty list it's a starting point.
  const competitors = (result.functionalCompetitor ?? []).filter(
    (s) => s.familyId || s.calibreId || s.name,
  );
  if (competitors.length > 0) {
    const kept =
      draft.competitorCount > 0
        ? competitors.filter((s) => !isLow(s.confidence))
        : competitors;
    if (kept.length < competitors.length) {
      heldBack.push(
        `${competitors.length - kept.length} functional competitor${
          competitors.length - kept.length === 1 ? '' : 's'
        }`,
      );
    }
    if (kept.length > 0) next.functionalCompetitor = kept;
  }

  return { next, heldBack };
}
